// src/services/rewardSettingsService.js
//
// The admin's coin and price settings (rewardSettings/main), read live and
// shared by every screen. functions/settings.js applies the same numbers on
// the server, so what a student sees is what they get.
//
//   useRewardSettings()          { settings, loaded } (also keeps the avatar
//                                maker's prices up to date)
//   saveRewardSettings(data)     admins only (firestore.rules)
//   trailCoins(settings)         the 6 daily-trail amounts
//   albumCoins(settings), dupCoins(settings)

import { useEffect, useState } from "react";
import { doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase/firebaseConfig";
import { setAvatarOverrides } from "../components/avatar/avatarCatalog";
import DATA from "../components/rewards/rewardsData.json";

// The built-in amounts (keep in step with RULES in functions/coins.js and
// rewardsData.json) — shown as the grey defaults in the settings panel.
export const DEFAULT_RULES = {
  game: { finish: 5, coinsPerCorrect: 2, maxFromQuiz: 30, quizPointsPerCoin: 10, maxFromBonus: 5, bonusPointsPerCoin: 250, replaySameDay: 3, dailyCap: 200 },
  firstPlay: { video: 5, music: 5, singalong: 5 },
  quest: { treasure: 15, treasuresPerDay: 2, maxBoardsPerQuest: 6 },
  gift: { maxPerGift: 20, perStudentPerDay: 50, maxStudents: 60 },
  trail: { coins: DATA.trail.coins },
  album: { coins: DATA.albums[0].reward.coins },
  duplicateCoins: DATA.duplicateCoins,
};

const shared = { settings: {}, loaded: false };
const listeners = new Set();
let stop = null;

const start = () => {
  if (stop) return;
  stop = onSnapshot(
    doc(db, "rewardSettings", "main"),
    (snap) => {
      shared.settings = (snap.exists() && snap.data()) || {};
      shared.loaded = true;
      setAvatarOverrides(shared.settings.avatar);
      listeners.forEach((f) => f({ ...shared }));
    },
    () => {
      shared.loaded = true;
      listeners.forEach((f) => f({ ...shared }));
    }
  );
};

export const useRewardSettings = () => {
  const [state, setState] = useState({ ...shared });
  useEffect(() => {
    listeners.add(setState);
    start();
    setState({ ...shared });
    return () => listeners.delete(setState);
  }, []);
  return state;
};

// Keeps the parts of the settings doc this save doesn't touch (coin packs,
// promotions…) — setDoc without merge would otherwise erase them.
export const saveRewardSettings = (data) => {
  const { updatedAt, updatedBy, ...rest } = shared.settings || {};
  return setDoc(doc(db, "rewardSettings", "main"), { ...rest, ...data, updatedAt: serverTimestamp(), updatedBy: getAuth().currentUser?.uid || "" });
};

/** Educator promotions (popups): { coinDeal: {...}, productCode: {...} } */
export const savePromos = (promos) =>
  setDoc(doc(db, "rewardSettings", "main"), { promos, updatedAt: serverTimestamp(), updatedBy: getAuth().currentUser?.uid || "" }, { merge: true });

const todayNY = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
/** Is a promo switched on and not past its end date? (same test as the server) */
export const promoLive = (p) => Boolean(p && p.on && (!p.until || String(p.until) >= todayNY()));

const n = (v, d) => {
  if (v === undefined || v === null || v === "") return d;
  const x = Math.floor(Number(v));
  return Number.isFinite(x) && x >= 0 ? x : d;
};
export const trailCoins = (s) => DATA.trail.coins.map((d, i) => n(s?.rules?.trail?.coins?.[i], d));
export const albumCoins = (s) => n(s?.rules?.album?.coins, DATA.albums[0].reward.coins);
export const dupCoins = (s) => n(s?.rules?.duplicateCoins, DATA.duplicateCoins);
